import { useSearchParams } from 'next/navigation'
import { useMemo } from 'react'

import {
  type OfferV11,
  type PimProductWithVariantsV1,
} from '@redteclab/api/clients/bully'

import { useProductPageContext } from '../../../product-page/components/productPageContext'
import {
  productAddToCartParamsMakeProductPageMainCtaParams,
  type ProductAddToCartProductPageMainCtaParams,
} from '../../../product-page/product-add-to-cart-params/productAddToCartParams'
import { useProductMaxSelectableQuantity } from '../../../product-page/product-order-limit/useProductMaxSelectableQuantity'

import { useExperimentOwnBrandsOverlayProductProperties } from './useExperimentOwnBrandsOverlayProductProperties'

export const useExperimentOwnBrandsOverlaySelectionOptions = (
  product: PimProductWithVariantsV1,
  offer: OfferV11 | undefined,
): {
  label: string
  value: number
}[] => {
  const maxSelectableQuantity = useProductMaxSelectableQuantity({
    offer,
    product,
  })

  const selectOptions = useMemo(() => {
    const options: { label: string; value: number }[] = []

    for (let index = 1; index <= maxSelectableQuantity; index += 1) {
      options.push({ label: index.toString(), value: index })
    }

    return options
  }, [maxSelectableQuantity])

  return selectOptions
}

export const useExperimentOwnBrandsOverlayBuildAddToCartParams = (
  quantity: number,
  product: PimProductWithVariantsV1,
): {
  disableCta: boolean
  isPrivatePrescriptionAndCartMetadataHasErx?: boolean
  params?: ProductAddToCartProductPageMainCtaParams
} => {
  const searchParams = useSearchParams()
  const queryId = searchParams.get('queryID') ?? ''
  const query = searchParams.get('query') ?? ''

  const { currentPrescriptionType, offers, upid } =
    useExperimentOwnBrandsOverlayProductProperties(product)
  const { channel, rxByPrincipleControls } = useProductPageContext()

  const result = useMemo(() => {
    const offerId = offers?.[0]?.offerId

    if (!offerId) {
      return {
        disableCta: true,
      }
    }

    const params = productAddToCartParamsMakeProductPageMainCtaParams({
      channel,
      offerId,
      prescriptionType: currentPrescriptionType,
      productId: upid,
      quantity,
      query,
      queryId,
      rxByPrincipleControls,
    })

    return {
      disableCta: false,
      params,
    }
  }, [
    offers,
    currentPrescriptionType,
    upid,
    query,
    queryId,
    quantity,
    channel,
    rxByPrincipleControls,
  ])

  return result
}
